import along from "@turf/along";
import bearing from "@turf/bearing";
import { lineString } from "@turf/helpers";
import length from "@turf/length";
import simplify from "@turf/simplify";
import { MercatorCoordinate } from "mapbox-gl";
import { useEffect } from "react";
import { Easing, interpolate, useCurrentFrame } from "remotion";
import { computeCameraPosition } from "../computeCameraPosition";
import { interpolateBearing, legHeadingAt } from "./legHeading";
import { isValidCoord, legGradient, legPhaseState, writeLegLine, writeTransportMarker } from "./legWrites";
import { arcAltitude, legProgress, motionFloor } from "../reelAltitudes";
import { CARDS_PER_STOP, CITY_LEG_KM, CITY_MAX_ALTITUDE } from "../reelConstants";

// Safe bearing calculation that handles invalid coordinates
const safeBearing = (from: any, to: any): number => {
	try {
		if (!from || !to) return 0;
		const fromCoord = Array.isArray(from) ? from : [from?.lng ?? from?.[0], from?.lat ?? from?.[1]];
		const toCoord = Array.isArray(to) ? to : [to?.lng ?? to?.[0], to?.lat ?? to?.[1]];

		if (!isValidCoord(fromCoord[0]) || !isValidCoord(fromCoord[1]) || !isValidCoord(toCoord[0]) || !isValidCoord(toCoord[1])) {
			return 0;
		}
		return bearing(fromCoord, toCoord);
	} catch {
		return 0;
	}
};

/** Fold a longitude back into [-180, 180]; the path keeps its continuous values. */
const wrapLongitude = (lng: number) => {
	if (!isValidCoord(lng)) return lng;
	return ((((lng + 180) % 360) + 360) % 360) - 180;
};

const PathSequence = ({ map, MAP_DURATION, reelData, path, index, startAltitude, entryAltitude, exitAltitude, startPitch, smooth = false, PATH_COLOR, scale }) => {
	const pathFrame = useCurrentFrame();
	const pathPhase = (pathFrame + 1) / MAP_DURATION;
	const routeDistance = length(path);
	const cameraPath = simplify(path);

	useEffect(() => {
		map?.setPaintProperty(`line-${index}`, "line-gradient", legGradient(PATH_COLOR, inkStop));
		//Destination name text color
	}, [PATH_COLOR]);

	if (!map) return null;

	const maxAltitude = 8500000;

	// Safe lineString creation - only if path has at least 2 points
	const prevPathCoords = index > 0 ? reelData?.[index - 1]?.path : undefined;
	const prevPath = prevPathCoords?.length >= 2 ? lineString(prevPathCoords) : undefined;
	const prevPathDistance = !!prevPath ? length(prevPath) : 200;

	// The arc begins and ends where the stops on either side of this leg sit, so the camera never has
	// to jump at a boundary: the climb out of a place and the descent into the next one happen here,
	// in flight, over half a leg each, instead of being crammed into the one second the stop lasts.
	// Both ends matter. Fixing only the departure leaves you climbing gracefully out of Koh Phangan
	// and still arriving in Los Angeles from 8,500 km up -- the same one-sided fix that made the
	// antimeridian and the intro altitude look repaired when they were not.
	//
	// The fallbacks below are for callers that pass neither: guess the entry from the previous leg's
	// length, and end where the leg itself flies.
	const actualStartAltitude = typeof entryAltitude === "number" && entryAltitude > 0
		? entryAltitude
		: prevPathDistance < CITY_LEG_KM ? startAltitude : prevPathDistance < 100 ? Math.max(2000, Math.min(50000, prevPathDistance * 1200)) : startAltitude;
	const actualExitAltitude = typeof exitAltitude === "number" && exitAltitude > 0 ? exitAltitude : startAltitude;

	// The mid-arc peak needs the same ceiling, or a 6.5 km hop still balloons to zoom 15 halfway and
	// the buildings drop out exactly in the middle of the leg.
	// Relative rather than absolute: the arc may rise by half again over the leg's own altitude, so
	// it stays gentle at any local scale. A flat ceiling only worked for one leg length -- on a 6 km
	// hop the peak used to balloon to zoom 15 and the buildings dropped out mid-leg.
	// The peak is the leg's own framing height -- exactly high enough to see the leg, and nothing
	// beyond that. It used to be `routeDistance / MAP_DURATION * altitudeModifier`, which has no
	// relation to what needs to fit on screen: on the 451 km hop from Bangkok to Koh Phangan that
	// asked for 751,687 m where the leg itself frames at 263,186 m, and the round trip up and back
	// down inside 36 frames is what read as broken. It still has to clear both ends, or the "arc" is
	// really a ramp with a kink in the middle.
	const arcCeiling = startAltitude * (routeDistance < CITY_LEG_KM ? 1.15 : 1);
	const calcAltitude = Math.max(actualStartAltitude, actualExitAltitude, Math.min(arcCeiling, maxAltitude));

	// One smooth curve, shared with the plan that decided how long this leg lasts -- if the two
	// disagreed about the shape, the pacing would be computed for a curve that is not the one drawn.
	// And a floor under it, from the same optics: never so low that the ground crossed in one frame is
	// most of what is on screen. The easing usually keeps us clear of this; it is here so that no
	// combination of a long leg and a short duration can outrun the map again.
	const kmThisFrame = routeDistance * Math.abs(legProgress(pathPhase) - legProgress(pathPhase - 1 / MAP_DURATION));
	const altitude = Math.max(
		arcAltitude(pathPhase, actualStartAltitude, calcAltitude, actualExitAltitude),
		Math.min(motionFloor(kmThisFrame, startPitch), calcAltitude)
	);

	const maxPitch = 20;
	const pitchAltitudeThreshold = 2400000;
	const pitch = interpolate(altitude, [pitchAltitudeThreshold, 4000000], [startPitch, maxPitch], {
		extrapolateLeft: "clamp",
		extrapolateRight: "clamp",
	});

	const cameraAltitude = altitude;

	// Progress along the leg used to start at 42% of its average speed from the very first frame, while
	// the altitude curve eases in from nothing. The two worked against each other: leaving Bottle Beach
	// the camera crossed 15 km per frame at 16,535 m, where it can see about 4 km -- four screenfuls in
	// one frame, and a map that renders as empty background. Both now build up together.
	const easedPath = legProgress(pathPhase);
	// The marker keeps its geodesic timing; the ink is converted into line-progress units so the two
	// stay on the same point. See geodesicToProjectedFraction in legWrites.ts.
	const { inkStop, markerPosition: alongMarkerPath } = legPhaseState(path, routeDistance, easedPath);
	const alongCameraPath = along(cameraPath, routeDistance * easedPath).geometry.coordinates;

	// Clear the stops behind us.
	//
	// These are Mapbox layers, not React elements, so when a MarkerSequence ends its layers simply
	// keep whatever opacity they were last given -- visible, for the rest of the film. Somebody hit
	// this with the transport marker and fixed it there ("Hide transport marker from previous
	// PathSequence", below) but never for the destinations, so every fan stayed up: leaving Schiphol
	// its photo cards hung in frame for the whole 8.8 s flight to Bangkok, and by the last leg several
	// fans overlapped each other and the logo.
	//
	// The stop we are leaving fades out over the first fifth of the leg rather than cutting, and every
	// other stop is forced down, which also clears anything stale from an earlier pass.
	const departingFade = interpolate(pathPhase, [0, 0.2], [1, 0], {
		extrapolateLeft: "clamp",
		extrapolateRight: "clamp",
		easing: Easing.inOut(Easing.ease),
	});
	const fadeStop = (stop: number, opacity: number) => {
		const set = (layerId: string, prop: string, value: number) => {
			try {
				if (map?.getLayer(layerId)) map.setPaintProperty(layerId, prop, value);
			} catch {
				// Layer was never created for this stop; nothing to hide.
			}
		};
		// The pulse is a transient: it swells to a 100 px ring and fades out inside the stop's own
		// second, and its radius stays large afterwards. Fading it FROM the departing opacity turns it
		// back on at full strength at that radius -- a big dark disc over the map at the start of every
		// leg. It is only ever off out here.
		set(`pulse-${stop}`, "circle-opacity", 0);
		set(`marker-${stop}`, "circle-opacity", opacity);
		set(`countText-${stop}`, "text-opacity", opacity);
		set(`countText-${stop}`, "icon-opacity", opacity);
		set(`destinationName-${stop}`, "text-opacity", opacity);
		set(`destinationName-${stop}`, "icon-opacity", opacity);
		set(`cardShadow-${stop}`, "icon-opacity", opacity);
		for (let card = 0; card < CARDS_PER_STOP; card++) set(`cardPhoto-${stop}-${card}`, "icon-opacity", opacity);
	};
	// Every stop, including the one we are flying towards -- it gets its own sequence when we land,
	// and leaving it untouched here means a stale value from an earlier pass can survive.
	for (let stop = 0; stop < (reelData?.length ?? 0); stop++) {
		fadeStop(stop, stop === index ? departingFade : 0);
	}

	if (map.getLayer(`line-${index}`)) {
		// The same writes FinishedSequences makes for a leg that is already over (legWrites.ts), so a tab that
		// starts after this leg leaves the map as a tab that ran it does.
		writeLegLine(map, index, PATH_COLOR, inkStop);
		writeTransportMarker(map, { vehicle: reelData?.[index]?.vehicle, scale, position: alongMarkerPath, opacity: 1 });
	}

	// Safe bearing calculations using safeBearing helper
	const currentPath = reelData?.[index]?.path;
	const nextPath = reelData?.[index + 1]?.path;

	// The bearing used to be divided by three, so the camera only ever hinted at the direction of
	// travel. A leg running due south got a camera facing 60 degrees, and the route then crossed the
	// frame sideways and left it at one edge while the next stop came in at the other. Facing along
	// the leg keeps the line running up the screen, which is what "following a route" looks like.
	const startHeading = currentPath?.length >= 2 ? safeBearing(currentPath[0], currentPath[currentPath.length - 1]) : 0;
	// Falling back to 0 pointed the camera due north on the second-to-last leg, whatever direction it
	// was actually travelling. Hold the current heading instead when there is no next leg to turn to.
	const endHeading = nextPath?.length >= 2 ? safeBearing(nextPath[0], nextPath[nextPath.length - 1]) : startHeading;

	// Turn late, not throughout. Interpolating the heading across the whole leg means the camera is
	// only aligned with the route at the moment it sets off: by halfway it is already facing the
	// *next* leg, and the line you are supposed to be following runs off the side of the frame. The
	// Than Sadet to Bottle Beach leg turns 60 degrees, and for most of it the route was barely in
	// shot. Hold the leg's own bearing until the last third, then ease into the next one on arrival.
	// Look along the road, not along the chord.
	//
	// `safeBearing(path[0], path[last])` is the straight line from where the leg starts to where it
	// ends, and on a winding leg that is a direction you never actually travel: the 29.8 km road from
	// Bottle Beach to Mae Haad runs south, then west, then north, while its chord points at 274. The
	// camera held that chord for the whole leg, so the route swept across the frame and left it.
	//
	// Take the bearing from where we are to a point further along instead. The look-ahead is what
	// keeps it smooth -- sampling the immediate next point would jitter on every bend -- and near the
	// end it naturally clamps to the final approach direction.
	// Ease in from the heading we arrived on, so the turn is spread over the first third of the flight
	// rather than crammed into the stop. After that it follows the leg's own heading as before.
	const ownHeading = legHeadingAt(path?.geometry?.coordinates, easedPath);
	const arrivedHeading = index > 0 ? legHeadingAt(reelData?.[index - 1]?.path, 1) : null;
	const entryPhase = interpolate(pathPhase, [0, 0.33], [0, 1], {
		extrapolateLeft: "clamp",
		extrapolateRight: "clamp",
		easing: Easing.inOut(Easing.ease),
	});
	const heading = arrivedHeading === null ? ownHeading : interpolateBearing(entryPhase, arrivedHeading, ownHeading);

	// The flattened antimeridian path deliberately runs past +/-180 so its length and progress stay
	// continuous. The camera cannot use those numbers raw: MercatorCoordinate.fromLngLat(195, ...)
	// puts the free camera in the next world copy, past the edge of tile coverage, and the frame
	// fills with the flat style background behind a dead-straight seam. Wrap it back for the camera
	// only, and leave the path itself continuous.
	const lngLat = {
		lng: wrapLongitude(alongCameraPath[0]),
		lat: alongCameraPath[1],
	};

	// compute corrected camera ground position, so that he leading edge of the path is in view
	const correctedPosition = computeCameraPosition(pitch, heading, lngLat, cameraAltitude);

	// set the pitch and bearing of the camera
	const camera = map.getFreeCameraOptions();
	camera.setPitchBearing(pitch, heading);

	// set the position and altitude of the camera
	camera.position = MercatorCoordinate.fromLngLat(correctedPosition, cameraAltitude);

	// apply the new camera options
	map.setFreeCameraOptions(camera);

	return <></>;
};

export default PathSequence;
